/**
 * Settings API
 *
 * Handles all settings-related API calls (settings, prompt profiles, provider credentials, delivery config).
 * Uses Zod validation for response types.
 */

import { z } from 'zod';
import { logError } from '@/lib/logger';
import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import {
  promptProfilesListResponseSchema,
  createProfileResponseSchema,
  updateProfileResponseSchema,
  settingsPreviewAnalyzeResponseSchema,
  providerCredentialsListResponseSchema,
  orgDeliveryConfigResponseSchema,
  orgDeliveryConfigPatchResponseSchema,
  deliveryTestResponseSchema,
  createAicaFieldsResponseSchema,
  listAicaFieldsResponseSchema,
  multiProviderDeliveryConfigResponseSchema,
  unconfiguredDeliveryConfigResponseSchema,
  dealStateCommentSettingSchema,
  getUserNotificationChannelsResponseSchema,
  updateUserNotificationChannelsResponseSchema,
  testNotificationChannelResponseSchema,
  dashboardResponseSchema,
  telegramLinkResponseSchema,
  retentionImpactResponseSchema,
} from '@aica/api-types/schemas';
import type {
  CreateAicaFieldsResponse,
  DashboardResponse,
  ListAicaFieldsResponse,
  OrgDeliveryConfigPatchResponse,
  OrgDeliveryConfigResponse,
  RetentionImpactResponse,
  TelegramLinkRequest,
} from '@aica/api-types';
import type {
  SettingsResponse,
  PromptProfile,
  PromptProfilesListResponse,
  PreviewAnalyzeRequest,
  PreviewAnalyzeResponse,
  ProviderCredentialsListResponse,
  CreateProviderCredentialRequest,
  DeliveryTestResponse,
  SuccessResponse,
  // Multi-provider types
  CrmProviderName,
  MultiProviderDeliveryConfigResponse,
  OrgCrmProviders,
  OrgCrmProvidersPatch,
  GenericFieldConfig,
  GenericFieldConfigPatch,
  AutofillFields,
} from '../types';
import type {
  GetUserNotificationChannelsResponse,
  UpdateUserNotificationChannelsRequest,
  UpdateUserNotificationChannelsResponse,
  TestNotificationChannelRequest,
  TestNotificationChannelResponse,
  ProfileBinding,
  ConfirmTelegramCodeRequest,
  TelegramLinkResponse,
} from '@aica/api-types';

export class SettingsApi extends BaseApiClient {
  // ============================================================================
  // GENERAL SETTINGS
  // ============================================================================

  async getSettings(): Promise<SettingsResponse> {
    // Generic Record<string, unknown> - no strict schema needed
    return this.request<SettingsResponse>(API_ENDPOINTS.SETTINGS);
  }

  async updateSettings(data: Record<string, unknown>): Promise<SettingsResponse> {
    return this.request<SettingsResponse>(API_ENDPOINTS.SETTINGS, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Preview a retention change: how many of this organization's records,
   * coaching notes, coaching sessions, assistant questions and deal states are
   * older than the proposed window and would be permanently deleted by the
   * next cleanup run (#2772, #2775). Owner-only (403 otherwise).
   */
  async getRetentionImpact(days: number): Promise<RetentionImpactResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.SETTINGS_RETENTION_IMPACT(days),
      retentionImpactResponseSchema
    );
  }

  // ============================================================================
  // DASHBOARD
  // ============================================================================

  async getDashboard(options?: {
    refresh?: boolean;
    period?: 'today' | 'week' | 'month' | 'quarter';
    /** @deprecated pass `projectIds` — ignored whenever it is present and non-empty. */
    projectId?: string;
    /** Multi-project filter (project-multiselect K7). Empty/omitted = all projects. */
    projectIds?: string[];
    periodStart?: string;
    periodEnd?: string;
    includeDemo?: 'yes' | 'no';
  }): Promise<DashboardResponse> {
    // Role-scoped dashboard — validated against dashboardResponseSchema (WS1-T7)
    // Pass ?refresh=true to bypass KV cache, ?period= for time range, ?projectIds= to filter by project(s)
    // ?periodStart= and ?periodEnd= for custom date range (period navigation)
    // ?includeDemo= to include/exclude seeded demo records (see useDemoFilter)
    const params = new URLSearchParams();
    if (options?.refresh) params.set('refresh', 'true');
    if (options?.period) params.set('period', options.period);
    // The edge-api route reads projectIds via `getAll()` (see dashboard.ts),
    // so a single repeated `set()` here is fine — CSV or one occurrence, the
    // shared projectIdsFilterSchema parses both.
    if (options?.projectIds && options.projectIds.length > 0) {
      params.set('projectIds', options.projectIds.join(','));
    } else if (options?.projectId) {
      params.set('projectId', options.projectId);
    }
    if (options?.periodStart) params.set('periodStart', options.periodStart);
    if (options?.periodEnd) params.set('periodEnd', options.periodEnd);
    if (options?.includeDemo) params.set('includeDemo', options.includeDemo);
    // Minutes east of UTC (WS1-T6) — buckets hour/day groupings (e.g. call
    // distribution byHour) in the user's local timezone instead of UTC.
    params.set('tzOffset', String(-new Date().getTimezoneOffset()));
    const qs = params.toString();
    const url = qs ? `${API_ENDPOINTS.DASHBOARD}?${qs}` : API_ENDPOINTS.DASHBOARD;
    return this.validatedRequest(url, dashboardResponseSchema);
  }

  // ============================================================================
  // PROMPT PROFILES
  // ============================================================================

  /**
   * Get prompt profiles - validated with Zod
   */
  async getPromptProfiles(): Promise<PromptProfilesListResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.PROMPT_PROFILES,
      promptProfilesListResponseSchema
    );
  }

  /**
   * Preview analyze - validated with Zod
   */
  async analyzePromptPreview(
    data: PreviewAnalyzeRequest
  ): Promise<PreviewAnalyzeResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.PROMPT_PROFILES_PREVIEW_ANALYZE,
      settingsPreviewAnalyzeResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Duplicate prompt profile - validated with Zod
   */
  async duplicatePromptProfile(id: string, name: string): Promise<PromptProfile> {
    const response = await this.validatedRequest(
      API_ENDPOINTS.PROMPT_PROFILES_DUPLICATE(id),
      createProfileResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({ name }),
      }
    );
    return response.profile as PromptProfile;
  }

  async deletePromptProfile(id: string): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.PROMPT_PROFILES_BY_ID(id), {
      method: 'DELETE',
    });
  }

  /**
   * Create prompt profile - validated with Zod
   */
  async createPromptProfile(data: {
    name: string;
    description?: string;
    baseCategory: string;
    toggles: Record<string, boolean | undefined>;
    binding?: string;
  }): Promise<PromptProfile> {
    const response = await this.validatedRequest(
      API_ENDPOINTS.PROMPT_PROFILES,
      createProfileResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return response.profile as PromptProfile;
  }

  /**
   * Restore the package default profile for a binding - validated with Zod
   * (after delete-as-opt-out). 409 if a profile with this binding already exists.
   */
  async restoreDefaultPromptProfile(binding: ProfileBinding): Promise<PromptProfile> {
    const response = await this.validatedRequest(
      API_ENDPOINTS.PROMPT_PROFILES_RESTORE_DEFAULT,
      createProfileResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({ binding }),
      }
    );
    return response.profile as PromptProfile;
  }

  /**
   * Update prompt profile - validated with Zod
   * Partial update - only send fields that changed
   */
  async updatePromptProfile(
    id: string,
    data: Partial<{
      name: string;
      description: string;
      baseCategory: string;
      toggles: Record<string, boolean | undefined>;
      // null unbinds, a value rebinds (409 if the binding is already in use)
      binding: string | null;
    }>
  ): Promise<PromptProfile> {
    const response = await this.validatedRequest(
      API_ENDPOINTS.PROMPT_PROFILES_BY_ID(id),
      updateProfileResponseSchema,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );
    return response.profile as PromptProfile;
  }

  // ============================================================================
  // PROVIDER CREDENTIALS
  // ============================================================================

  /**
   * Get provider credentials - validated with Zod
   */
  async getProviderCredentials(orgId: string): Promise<ProviderCredentialsListResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.PROVIDER_CREDENTIALS(orgId),
      providerCredentialsListResponseSchema
    );
  }

  async createProviderCredential(
    orgId: string,
    data: CreateProviderCredentialRequest
  ): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.PROVIDER_CREDENTIALS(orgId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteProviderCredential(
    orgId: string,
    type: string,
    name: string
  ): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(
      API_ENDPOINTS.PROVIDER_CREDENTIALS_BY_TYPE(orgId, type, name),
      {
        method: 'DELETE',
      }
    );
  }

  // ============================================================================
  // DELIVERY CONFIG
  // ============================================================================

  /**
   * Get delivery config - validated with Zod
   *
   * Returns the organization's multi-provider delivery settings and its recent
   * deliveries (`orgDeliveryConfigResponseSchema`).
   */
  async getDeliveryConfig(orgId: string): Promise<OrgDeliveryConfigResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.DELIVERY_CONFIG(orgId),
      orgDeliveryConfigResponseSchema
    );
  }

  /**
   * Test delivery - validated with Zod. Takes the same body as the PATCH of the
   * delivery config (nothing is written).
   */
  async testDelivery(
    orgId: string,
    config: {
      activeCrmProvider?: CrmProviderName;
      crmProviders?: OrgCrmProviders;
      genericFieldConfig?: GenericFieldConfig;
    }
  ): Promise<DeliveryTestResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.DELIVERY_TEST(orgId),
      deliveryTestResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(config),
      }
    );
  }

  // ============================================================================
  // MULTI-PROVIDER CRM CONFIG (NEW)
  // ============================================================================

  /**
   * Get multi-provider delivery config - validated with Zod
   *
   * Returns active CRM provider, provider-specific configs, and generic field config;
   * `null` when the organization has no multi-provider settings yet
   * (`configured: false`) — nothing to configure, not a broken response.
   */
  async getMultiProviderDeliveryConfig(
    orgId: string
  ): Promise<MultiProviderDeliveryConfigResponse | null> {
    const response = await this.validatedRequest(
      API_ENDPOINTS.DELIVERY_CONFIG(orgId),
      z.union([
        unconfiguredDeliveryConfigResponseSchema,
        multiProviderDeliveryConfigResponseSchema,
      ])
    );
    return 'configured' in response ? null : response;
  }

  /**
   * PATCH the delivery config — only the keys a writer owns (#2987): a provider
   * may be partial (the field table sends `field_mappings`, the destination
   * cards `config`), and what the body lacks the server keeps. Inside
   * `field_mappings` and `genericFieldConfig` the server merges key by key
   * (#3001): send only the changed keys; a `null` mapping removes the key.
   *
   * Returns the deal-state comment the server says it STORED (`null` when the
   * response does not carry a boolean there): an edge-api rolled back behind the
   * UI strips the unknown key and answers 200 without it, so the caller must
   * not take the requested value for the stored one.
   *
   * `stored` is the whole config the server answers after merging the body
   * (#3025) — the flags another writer saved meanwhile included; `null` when
   * the answer does not parse, so the caller refetches instead of guessing.
   */
  async updateMultiProviderDeliveryConfig(
    orgId: string,
    data: {
      activeCrmProvider?: CrmProviderName;
      crmProviders?: OrgCrmProvidersPatch;
      genericFieldConfig?: GenericFieldConfigPatch;
      autofillFields?: AutofillFields;
      dealStateComment?: boolean;
    }
  ): Promise<{
    dealStateComment: boolean | null;
    stored: OrgDeliveryConfigPatchResponse | null;
  }> {
    const response = await this.request<Record<string, unknown> | null>(
      API_ENDPOINTS.DELIVERY_CONFIG(orgId),
      { method: 'PATCH', body: JSON.stringify(data) }
    );
    const comment = dealStateCommentSettingSchema.safeParse(response?.dealStateComment);
    const stored = orgDeliveryConfigPatchResponseSchema.safeParse(response);
    if (!stored.success) {
      // The save itself succeeded; the caller refetches. Logged, so a drift of
      // the contract is not hidden behind an extra GET on every save.
      logError('API validation failed', {
        component: 'SettingsApi.updateMultiProviderDeliveryConfig',
        endpoint: API_ENDPOINTS.DELIVERY_CONFIG(orgId),
        issues: stored.error.issues.map((issue) => ({
          path: issue.path.join('.'),
          message: issue.message,
          code: issue.code,
        })),
      });
    }
    return {
      dealStateComment: comment.success ? comment.data : null,
      stored: stored.success ? stored.data : null,
    };
  }

  /**
   * Get Smart Process info for org (Clerk auth)
   */
  async getOrgSmartProcess(orgId: string): Promise<{ entityTypeId: string | null }> {
    return this.request<{ entityTypeId: string | null }>(
      API_ENDPOINTS.SMART_PROCESS_ORG(orgId)
    );
  }

  /**
   * Setup (create) Smart Process for org (Clerk auth, idempotent)
   */
  async setupOrgSmartProcess(orgId: string): Promise<{
    success: boolean;
    entityTypeId: string;
    fieldsCreated?: number;
    totalFields?: number;
  }> {
    return this.request<{
      success: boolean;
      entityTypeId: string;
      fieldsCreated?: number;
      totalFields?: number;
    }>(API_ENDPOINTS.SMART_PROCESS_ORG_SETUP(orgId), { method: 'POST' });
  }

  /**
   * Recreate Smart Process (clears old, creates new)
   */
  async recreateOrgSmartProcess(orgId: string): Promise<{
    success: boolean;
    entityTypeId: string;
    fieldsCreated?: number;
    totalFields?: number;
  }> {
    return this.request<{
      success: boolean;
      entityTypeId: string;
      fieldsCreated?: number;
      totalFields?: number;
    }>(API_ENDPOINTS.SMART_PROCESS_ORG_RECREATE(orgId), { method: 'POST' });
  }

  /**
   * Setup (repair/create) UF_ fields on an existing Smart Process
   */
  async setupOrgSmartProcessFields(orgId: string): Promise<{
    success: boolean;
    entityTypeId: string;
    fieldsCreated: number;
    totalFields: number;
    warning?: string;
  }> {
    return this.request<{
      success: boolean;
      entityTypeId: string;
      fieldsCreated: number;
      totalFields: number;
      warning?: string;
    }>(API_ENDPOINTS.SMART_PROCESS_ORG_SETUP_FIELDS(orgId), { method: 'POST' });
  }

  /**
   * Sync CRM users to AICA user_external_ids (match by email)
   */
  async syncCrmUsers(orgId: string): Promise<{
    synced: number;
    skipped: string[];
    total: number;
  }> {
    return this.request<{ synced: number; skipped: string[]; total: number }>(
      API_ENDPOINTS.SYNC_CRM_USERS(orgId),
      { method: 'POST' }
    );
  }

  /**
   * Create Smart Process in Bitrix24
   */
  async createSmartProcess(
    _orgId: string
  ): Promise<{ success: boolean; entityTypeId?: string; error?: string }> {
    return this.request<{ success: boolean; entityTypeId?: string; error?: string }>(
      API_ENDPOINTS.SMART_PROCESS_SETUP,
      { method: 'POST' }
    );
  }

  /**
   * Create the Aelo fields in Bitrix24 - validated with Zod. Without an
   * `entityType` the server creates them on every card type delivery writes to.
   */
  async createBitrix24Fields(fields: string[]): Promise<CreateAicaFieldsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.BITRIX24_FIELDS,
      createAicaFieldsResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({ fields }),
      }
    );
  }

  /**
   * Which Aelo fields Bitrix24 already carries (on every card type) - validated with Zod
   */
  async listBitrix24Fields(): Promise<ListAicaFieldsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.BITRIX24_FIELDS,
      listAicaFieldsResponseSchema
    );
  }

  // ============================================================================
  // USER NOTIFICATION CHANNELS
  // ============================================================================

  /**
   * Get current user's notification channels - validated with Zod
   */
  async getUserNotificationChannels(): Promise<GetUserNotificationChannelsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.NOTIFICATION_CHANNELS,
      getUserNotificationChannelsResponseSchema
    );
  }

  /**
   * Update current user's notification channels - validated with Zod
   */
  async updateUserNotificationChannels(
    data: UpdateUserNotificationChannelsRequest
  ): Promise<UpdateUserNotificationChannelsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.NOTIFICATION_CHANNELS,
      updateUserNotificationChannelsResponseSchema,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Test a notification channel - validated with Zod
   */
  async testNotificationChannel(
    data: TestNotificationChannelRequest
  ): Promise<TestNotificationChannelResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.NOTIFICATION_CHANNELS_TEST,
      testNotificationChannelResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Generate a new webhook secret
   */
  async generateWebhookSecret(): Promise<{ success: boolean; secret: string }> {
    return this.request<{ success: boolean; secret: string }>(
      API_ENDPOINTS.NOTIFICATION_CHANNELS_GENERATE_SECRET,
      {
        method: 'POST',
      }
    );
  }

  /**
   * Issue a one-shot Telegram deep link for the current user (Task 8).
   * The caller opens `url` in a new tab and presses Start; the chat id is
   * bound in the background by the Telegram webhook (Task 9), not by this
   * call — it only starts the flow.
   */
  async linkTelegram(): Promise<TelegramLinkResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.INTEGRATIONS_TELEGRAM_LINK,
      telegramLinkResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({} satisfies TelegramLinkRequest),
      }
    );
  }

  /**
   * Bind Telegram via the 6-digit pairing code (Task 9) — fallback for a
   * user who opened the bot directly instead of following the deep link.
   * Unlike `linkTelegram`, this resolves synchronously with the updated
   * channel state.
   */
  async confirmTelegramCode(
    data: ConfirmTelegramCodeRequest
  ): Promise<UpdateUserNotificationChannelsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.NOTIFICATION_CHANNELS_TELEGRAM_CONFIRM_CODE,
      updateUserNotificationChannelsResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }
}
